import { useState, useEffect } from 'react'
import { message } from 'antd'

const useEducation = () => {
  const [loading, setLoading] = useState(false)
  const [resources, setResources] = useState([])

  useEffect(() => {
    const fetchResources = async () => {
      setLoading(true)
      try {
        // Simulate API call to fetch education resources
        await new Promise((resolve) => setTimeout(resolve, 800))

        // Normally we would fetch from an API
        // For now, we'll use the resources from the local mock data
        let response = []
        try {
          const module = await import('../utils/educationResources.json')
          if (module && module.default) {
            response = module.default
          } else {
            console.error('Error loading educationResources.json: Module or default export not found')
            message.error('Failed to load education resources data')
          }
        } catch (importError) {
          console.error('Error importing educationResources.json:', importError)
          message.error('Failed to load education resources data')
        }

        setResources(response)
      } catch (error) {
        console.error('Error fetching education resources:', error)
        message.error('Failed to load education resources')
      } finally {
        setLoading(false)
      }
    }

    fetchResources()
  }, [])

  const addResource = async (resourceData) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 600))

      const newResource = {
        id: `edu-resource-${Date.now()}`,
        ...resourceData
      }

      setResources([...resources, newResource])
      return newResource
    } catch (error) {
      console.error('Error adding education resource:', error)
      message.error('Failed to add education resource')
      return null
    } finally {
      setLoading(false)
    }
  }

  const updateResource = async (resourceId, resourceData) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 600))

      const updatedResources = resources.map((resource) => {
        if (resource.id === resourceId) {
          return { ...resource, ...resourceData }
        }
        return resource
      })

      setResources(updatedResources)
      return true
    } catch (error) {
      console.error('Error updating education resource:', error)
      message.error('Failed to update education resource')
      return false
    } finally {
      setLoading(false)
    }
  }

  const deleteResource = async (resourceId) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))

      const updatedResources = resources.filter((resource) => resource.id !== resourceId)
      setResources(updatedResources)
      return true
    } catch (error) {
      console.error('Error deleting education resource:', error)
      message.error('Failed to delete education resource')
      return false
    } finally {
      setLoading(false)
    }
  }

  const trackResourceCompletion = async (resourceId, patientId, completed) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 400))

      // In a real app, we would send this tracking information to the backend

      return {
        id: `edu-tracking-${Date.now()}`,
        resourceId,
        patientId,
        completed,
        timestamp: new Date().toISOString()
      }
    } catch (error) {
      console.error('Error tracking resource completion:', error)
      message.error('Failed to track resource completion')
      return null
    } finally {
      setLoading(false)
    }
  }

  const getResourcesByFormat = (format) => {
    return resources.filter((resource) => resource.format.toLowerCase() === format.toLowerCase())
  }

  const getResourcesByType = (type) => {
    return resources.filter((resource) => resource.type === type)
  }

  return {
    loading,
    resources,
    addResource,
    updateResource,
    deleteResource,
    trackResourceCompletion,
    getResourcesByFormat,
    getResourcesByType
  }
}

export default useEducation
